"use client";

import React, { useState } from "react";
import Link from "next/link";
import { api } from "~/utils/api";
import { useMe } from "~/hooks/useMe";
import { useCourseContext } from "~/contexts/CourseContext";
import { FilledButton } from "~/components/buttons/filled-button";
import { OutlineButton } from "~/components/buttons/outline-button";
import ConfirmationModal from "~/components/modal/confirmation-modal";
import { Close } from "~/components/icons/close";
import { DeleteIcon } from "~/components/icons/delete";
import { Spinner } from "~/components/loading/spinner";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { NameYourPriceOfferStatus } from "@golf-district/shared";

const RECENT_OFFERS_WINDOW_DAYS = 7;

const STATUS_LABELS: Record<NameYourPriceOfferStatus, { label: string; className: string }> = {
  [NameYourPriceOfferStatus.PENDING]: { label: "Pending", className: "bg-yellow-100 text-yellow-700 border border-yellow-300" },
  [NameYourPriceOfferStatus.ACCEPTED]: { label: "Accepted", className: "bg-green-100 text-green-700 border border-green-300" },
  [NameYourPriceOfferStatus.DECLINED]: { label: "Declined", className: "bg-red-100 text-red-700 border border-red-300" },
  [NameYourPriceOfferStatus.EXPIRED]: { label: "Expired", className: "text-gray-500 border border-gray-300" },
  [NameYourPriceOfferStatus.WITHDRAWN]: { label: "Withdrawn", className: "text-gray-500 border border-gray-300" },
};
// A Pending offer that's already been fanned out to at least one seller (still
// awaiting their response) — distinct from a Pending offer that hasn't matched
// anyone yet, which keeps the plain "Pending" label above.
const ACTIVE_MATCH_STATUS = { label: "Active", className: "bg-blue-100 text-blue-700 border border-blue-300" };

const formatTime = (value: number) => {
  const hour = Math.floor(value / 100);
  const minute = value % 100;
  const h = hour % 12 === 0 ? 12 : hour % 12;
  const period = hour < 12 ? "AM" : "PM";
  return `${h}:${minute === 0 ? "00" : minute} ${period}`;
};

type Offer = {
  id: string;
  date: string;
  startTime: number;
  endTime: number;
  players: number;
  pricePerPlayer: number;
  status: NameYourPriceOfferStatus;
  createdDateTime: string;
  courseId?: string;
  hasActiveMatch: boolean;
};

export function YourOffers() {
  const { user, isLoading: isUserLoading } = useMe();
  const { course } = useCourseContext();
  const [withdrawOfferId, setWithdrawOfferId] = useState<string | null>(null);
  const [removeOfferId, setRemoveOfferId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = api.nameYourPriceOffer.getMyOffers.useQuery(
    {},
    { enabled: !!user?.id }
  );

  const { mutateAsync: deleteOffer } =
    api.nameYourPriceOffer.deleteOffer.useMutation();

  // The API returns status as a plain number; treat it as the enum here at the one
  // boundary point rather than casting at every comparison site below.
  const allOffers: Offer[] = (data?.data ?? []) as Offer[];
  // This tab only surfaces recent activity. PENDING offers are always shown no
  // matter how old — a buyer may submit weeks ahead of the play date, and hiding a
  // still-live offer would leave them no way to see or withdraw it (an unmatched
  // NyP offer has no bookingOffer row, so it wouldn't appear under My Tee Box >
  // Offers either). The 7-day created-date window only applies to resolved offers
  // (accepted / declined / expired / withdrawn).
  const recentCutoff = dayjs().subtract(RECENT_OFFERS_WINDOW_DAYS, "day");
  const offers = allOffers.filter(
    (offer) =>
      offer.status === NameYourPriceOfferStatus.PENDING ||
      dayjs(offer.createdDateTime).isAfter(recentCutoff)
  );

  const handleDeleteSingle = async (id: string, isPendingOffer = false) => {
    setDeletingId(id);
    setRowErrors((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    await deleteOffer(
      { offerId: id, redirectHref: window.location.origin },
      {
        onSuccess: () => {
          toast.success(isPendingOffer ? "Offer withdrawn successfully." : "Offer removed.", {
            progressStyle: { background: "var(--primary-color)" },
          });
          void refetch();
        },
        onError: () => {
          setRowErrors((prev) => ({
            ...prev,
            [id]: isPendingOffer ? "Failed to withdraw offer." : "Failed to remove offer.",
          }));
        },
      }
    );
    setDeletingId(null);
  };

  if (!user && !isUserLoading) {
    return (
      <p className="text-center text-[0.875rem] text-primary-gray py-6">
        Please log in to view your offers.
      </p>
    );
  }

  return (
    <section className="mt-6 pb-12">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-[1.25rem] capitalize text-secondary-black md:text-[2rem] flex items-center gap-6">
          Your offers
        </h1>
      </div>

      <p className="text-[0.8125rem] text-primary-gray mb-3">
        Showing offers from the last {RECENT_OFFERS_WINDOW_DAYS} days. For older offers, go to{" "}
        <Link href={`/${course?.id}/my-tee-box?section=offers`} className="text-[var(--primary-color)] underline">
          My Tee Box &gt; Offers
        </Link>
        .
      </p>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="w-[3.125rem] h-[3.125rem]" />
        </div>
      ) : isError ? (
        <p className="text-center text-[0.875rem] text-red-500 py-6">
          Failed to load offers. Please try again.
        </p>
      ) : offers.length === 0 ? (
        <p className="text-center text-[0.875rem] text-primary-gray py-6">
          {allOffers.length === 0
            ? "You have no offers yet."
            : `No offers in the last ${RECENT_OFFERS_WINDOW_DAYS} days.`}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-[0.8125rem]">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left font-medium text-primary-gray">
                  Offer created date time
                </th>
                <th className="px-4 py-3 text-left font-medium text-primary-gray">
                  PlayDate
                </th>
                <th className="px-4 py-3 text-left font-medium text-primary-gray">
                  Time window
                </th>
                <th className="px-4 py-3 text-left font-medium text-primary-gray">
                  Players
                </th>
                <th className="px-4 py-3 text-left font-medium text-primary-gray">
                  Offer / player
                </th>
                <th className="px-4 py-3 text-left font-medium text-primary-gray">
                  Status
                </th>
                <th className="px-3 py-3 text-left font-medium text-primary-gray">Actions</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((offer) => {
                // A countered sibling never changes this offer's own status badge — it
                // always reflects its actual DB status for consistency, regardless of
                // whether one of the matched sellers happened to counter.
                const statusInfo =
                  offer.status === NameYourPriceOfferStatus.PENDING && offer.hasActiveMatch
                    ? ACTIVE_MATCH_STATUS
                    : STATUS_LABELS[offer.status] ?? STATUS_LABELS[NameYourPriceOfferStatus.PENDING]!;
                // A countered sibling still leaves status === PENDING in the DB — withdrawing
                // has the same real effect (void hold + cancel siblings), so it must show the same
                // Withdraw confirmation as Pending, not the plain trash icon. Matches how the
                // unsolicited-offer flow treats COUNTER inside CAN_WITHDRAW_OR_CHANGE_OFFER.
                const isPending = offer.status === NameYourPriceOfferStatus.PENDING;
                // Accepted = a real booking exists — no delete, keep it permanently visible.
                const isAccepted = offer.status === NameYourPriceOfferStatus.ACCEPTED;
                // Expired — no delete either, same as accepted.
                const isExpired = offer.status === NameYourPriceOfferStatus.EXPIRED;
                return (
                  <tr
                    key={offer.id}
                    className="border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-secondary-black">
                      {dayjs(offer.createdDateTime).format("MMM D, h:mm A")}
                    </td>
                    <td className="px-4 py-3 font-medium text-secondary-black">
                      {dayjs(offer.date).format("ddd, MMM D")}
                    </td>
                    <td className="px-4 py-3 text-secondary-black">
                      {formatTime(offer.startTime)} – {formatTime(offer.endTime)}
                    </td>
                    <td className="px-4 py-3 text-secondary-black">
                      {offer.players}
                    </td>
                    <td className="px-4 py-3 font-medium text-secondary-black">
                      ${(offer.pricePerPlayer / 100).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block w-fit rounded-full px-2.5 py-0.5 text-[0.75rem] font-medium ${statusInfo.className}`}
                      >
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {isPending ? (
                        // Pending offers — show Withdraw button matching existing Offers Sent UI
                        <button
                          onClick={() => setWithdrawOfferId(offer.id)}
                          disabled={deletingId === offer.id}
                          className="rounded-full bg-[var(--primary-color)] px-3 py-1 text-[0.75rem] font-semibold text-white disabled:opacity-50 transition-opacity"
                        >
                          {deletingId === offer.id ? "..." : "Withdraw"}
                        </button>
                      ) : isAccepted || isExpired ? (
                        // Accepted / Expired — no delete allowed
                        <span className="text-[0.75rem] text-gray-400">No action</span>
                      ) : (
                        // Declined / Withdrawn — trash icon for UI cleanup
                        <button
                          onClick={() => setRemoveOfferId(offer.id)}
                          disabled={deletingId === offer.id}
                          className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-40"
                          aria-label="Remove offer"
                        >
                          {deletingId === offer.id ? <Spinner className="w-[0.875rem] h-[0.875rem]" /> : <DeleteIcon width="0.875rem" />}
                        </button>
                      )}
                      {rowErrors[offer.id] && (
                        <p className="text-red text-[0.6875rem] mt-1 whitespace-nowrap">{rowErrors[offer.id]}</p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Withdraw confirmation modal — shared ConfirmationModal, same title/
          buttons/icon as My Tee Box's Offers Sent withdraw modal so the two
          can't drift apart again. */}
      <ConfirmationModal
        isOpen={!!withdrawOfferId}
        onClose={async (confirmed) => {
          const id = withdrawOfferId;
          setWithdrawOfferId(null);
          if (confirmed && id) await handleDeleteSingle(id, true);
        }}
        title="Withdraw this offer?"
        description="Are you sure you want to withdraw this offer? The pre-authorization hold on your card will be released. This cannot be undone."
        confirmLabel="Withdraw"
        variant="info"
      />
      {/* Remove confirmation modal — non-pending offers, UI cleanup only */}
      {removeOfferId && (
        <>
          <div
            className="fixed left-0 top-0 z-20 h-[100dvh] w-screen backdrop-blur"
            onClick={() => setRemoveOfferId(null)}
          >
            <div className="h-screen bg-[#00000099]" />
          </div>
          <div className="fixed top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[26rem] rounded-xl bg-white p-6 shadow-xl">
            <Close
              className="absolute right-4 top-4 cursor-pointer"
              height={20}
              width={20}
              onClick={() => setRemoveOfferId(null)}
            />
            <h2 className="text-[1.125rem] font-semibold mb-2">Remove this offer?</h2>
            <p className="text-[0.875rem] text-primary-gray mb-6">
              This only removes it from your offers list — it won&apos;t affect
              any booking made from it. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <FilledButton
                onClick={async () => {
                  const id = removeOfferId;
                  setRemoveOfferId(null);
                  await handleDeleteSingle(id);
                }}
                disabled={deletingId === removeOfferId}
                className="flex-1 py-2 text-[0.875rem] disabled:opacity-50"
              >
                {deletingId === removeOfferId ? "Removing..." : "Yes, remove"}
              </FilledButton>
              <OutlineButton
                onClick={() => setRemoveOfferId(null)}
                className="flex-1 py-2 text-[0.875rem] border border-gray-300"
              >
                Cancel
              </OutlineButton>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
